import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtMYTDate } from "@/lib/myt";

/** Dedicated Expert Advisor Requests page — the exact existing IB EA claim review
 *  functionality, moved from the Overview into its own sidebar destination. */
export default function IBEARequests() {
  const [claims, setClaims] = useState(null);
  const [decision, setDecision] = useState(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");

  const load = () => {
    api.get("/ib/ea-claims").then((res) => setClaims(res.data)).catch(() => setClaims([]));
  };

  useEffect(() => {
    load();
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = claims === null ? null : !q ? claims : claims.filter((r) =>
    [r.member_uid, r.member_name, r.member_email, r.ea_name].some((v) => (v || "").toLowerCase().includes(q))
  );

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/ib/ea-claims/${decision.id}/decision`, {
        action: decision.action,
        remark: decision.remark,
      });
      toast.success(`EA claim ${decision.action === "approve" ? "approved — forwarded to admin" : "rejected"}`);
      setDecision(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="ib-ea-requests-page">
      <div>
        <p className="eyebrow-label">Requests</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mt-2">Expert Advisor Requests</h1>
      </div>
      <div className="card-lux overflow-hidden" data-testid="ib-ea-claims-section">
        <div className="px-6 pt-6">
          <p className="eyebrow-label">Expert Advisor Requests</p>
          <p className="mt-1 text-xs text-[#64748B]">EA claims from clients registered under you. Final access is granted by administration.</p>
          <div className="relative sm:max-w-sm mt-4">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search client by UID, username, name or email…"
              className="input-lux w-full text-sm pr-9"
              data-testid="ib-ea-search-input"
            />
            {query && (
              <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white transition-colors" data-testid="ib-ea-search-clear" aria-label="Clear search">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm" data-testid="ib-ea-claims-table">
            <thead>
              <tr className="border-b border-[#222530] text-left">
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Client</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Expert Advisor</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden md:table-cell">Broker / Rank</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden sm:table-cell">Requested</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Status</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#222530]">
              {filtered === null ? (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-[#64748B]">Loading EA claims...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-[#64748B]" data-testid="ib-ea-claims-empty">{q ? "No requests match your search." : "No Expert Advisor requests from your clients."}</td></tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.id} className="hover:bg-[#0B0C10]/60 transition-colors" data-testid={`ib-ea-claim-row-${r.id.slice(-6)}`}>
                    <td className="px-6 py-4">
                      <p className="text-white font-medium">{r.member_name}</p>
                      <p className="text-xs text-[#64748B] font-mono">{r.member_uid}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-white">{r.ea_name}</p>
                      {r.ea_requirements && <p className="text-[10px] text-[#64748B] mt-0.5 max-w-[220px] truncate" title={r.ea_requirements}>Requires: {r.ea_requirements}</p>}
                    </td>
                    <td className="px-6 py-4 text-[#94A3B8] hidden md:table-cell">
                      <p className="text-xs">{r.member_broker || "—"}</p>
                      <p className="text-[10px] text-[#64748B]">{r.member_rank || "—"}</p>
                    </td>
                    <td className="px-6 py-4 text-[#94A3B8] hidden sm:table-cell">{r.created_at ? fmtMYTDate(r.created_at) : "—"}</td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${
                        r.status === "ib_review" ? "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30"
                        : r.status === "pending_admin" || r.status === "approved" ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/30"
                        : "text-red-400 bg-red-400/10 border-red-400/30"
                      }`} data-testid={`ib-ea-claim-status-${r.id.slice(-6)}`}>
                        {r.status === "ib_review" ? "Awaiting Your Review" : r.status === "pending_admin" ? "Approved — Admin Review" : r.status === "approved" ? "Approved" : "Rejected"}
                      </span>
                      {r.ib_remark && <p className="text-[10px] text-[#64748B] mt-1">Your remark: {r.ib_remark}</p>}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {r.status === "ib_review" && (
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          <button onClick={() => setDecision({ id: r.id, action: "approve", remark: "" })} className="text-xs text-emerald-400 border border-emerald-400/30 hover:bg-emerald-400/10 rounded-lg px-2.5 py-1.5 transition-colors" data-testid={`ib-ea-approve-${r.id.slice(-6)}`}>
                            Approve
                          </button>
                          <button onClick={() => setDecision({ id: r.id, action: "reject", remark: "" })} className="text-xs text-red-400 border border-red-400/30 hover:bg-red-400/10 rounded-lg px-2.5 py-1.5 transition-colors" data-testid={`ib-ea-reject-${r.id.slice(-6)}`}>
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {decision && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="ib-ea-decision-modal">
            <div className="absolute inset-0 bg-black/70" onClick={() => setDecision(null)} />
            <div className="card-lux relative w-full max-w-md p-6 sm:p-8">
              <p className="eyebrow-label mb-2">{decision.action === "approve" ? "Approve EA Claim" : "Reject EA Claim"}</p>
              <h3 className="font-heading text-lg font-semibold text-white mb-6">Expert Advisor Request</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                    Remark {decision.action === "reject" ? <span className="text-red-400">*</span> : <span className="text-[#64748B]">(optional)</span>}
                  </label>
                  <textarea className="input-lux min-h-[80px]" value={decision.remark} onChange={(e) => setDecision((d) => ({ ...d, remark: e.target.value }))} data-testid="ib-ea-decision-remark-input" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={submit} disabled={busy || (decision.action === "reject" && decision.remark.trim().length < 2)} className={`flex-1 py-2.5 text-sm rounded-lg font-medium transition-colors disabled:opacity-60 ${
                    decision.action === "approve" ? "btn-gold" : "bg-red-500/90 hover:bg-red-500 text-white"
                  }`} data-testid="ib-ea-decision-confirm-button">
                    {busy ? "Submitting..." : decision.action === "approve" ? "Approve & Forward to Admin" : "Confirm Rejection"}
                  </button>
                  <button onClick={() => setDecision(null)} className="btn-outline-gold px-5 py-2.5 text-sm" data-testid="ib-ea-decision-cancel-button">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
